/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-anomaly.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * One detector per signal, never one detector for everything — a 40% drop in row count and a 40%
 * drop in a quality score are not the same event and must not share a threshold. The asymmetries
 * are deliberate: `volume` watches both directions, because a spike is a load that ran twice;
 * `freshness` watches only staleness, because data arriving *early* is not an incident and
 * flagging it is how a monitor earns a reputation for noise.
 */
export type SignalKind = 'volume' | 'freshness' | 'quality_score';

export const SIGNAL_KINDS: readonly SignalKind[] = ['volume', 'freshness', 'quality_score'];

export function isSignalKind(value: unknown): value is SignalKind {
  return (['volume', 'freshness', 'quality_score'] as readonly unknown[]).includes(value);
}

/**
 * Four members, and the splits matter more than the count. `insufficient_history` — no baseline
 * yet. **Never reported as normal**, and this is the cold-start suppression: a new dataset
 * produces silence until there is something to compare against. `undetermined` — a baseline exists
 * and the statistic cannot be computed from it, because the series is perfectly flat and the
 * deviation measure is zero. Its own verdict because "we looked and it was fine" and "the maths
 * did not apply" send an engineer to different places; folding it into `anomalous` would fire on a
 * table that loads exactly 1,000 rows daily the first time it loads 1,001. **Only `anomalous`
 * opens an incident.** The other three are silence.
 */
export type Verdict = 'insufficient_history' | 'normal' | 'undetermined' | 'anomalous';

export const VERDICTS: readonly Verdict[] = ['insufficient_history', 'normal', 'undetermined', 'anomalous'];

export function isVerdict(value: unknown): value is Verdict {
  return (['insufficient_history', 'normal', 'undetermined', 'anomalous'] as readonly unknown[]).includes(value);
}

/**
 * How urgently a finding deserves a person's attention, derived from `verdict` and `critical`
 * alone — never a separate judgement a finding could disagree with its own verdict about. Ordered
 * so a screen sorting by `priority_rank` puts what needs looking at first at the top: `critical`,
 * then `anomalous`, then `worth_reviewing` (a flat series the maths could not judge —
 * `undetermined`'s own reason for existing, applied to an ordering), then `normal`, then
 * `not_yet_measured` (`insufficient_history` — informational, not a finding about the data).
 */
export type Priority = 'critical' | 'anomalous' | 'worth_reviewing' | 'normal' | 'not_yet_measured';

export const PRIORITYS: readonly Priority[] = ['critical', 'anomalous', 'worth_reviewing', 'normal', 'not_yet_measured'];

export function isPriority(value: unknown): value is Priority {
  return (['critical', 'anomalous', 'worth_reviewing', 'normal', 'not_yet_measured'] as readonly unknown[]).includes(value);
}

/**
 * Who wrote the words. A model's sentence and a template's must never render identically — the
 * same reason `CandidateSource` and `ColumnVerdict.method` exist.
 */
export type ExplanationSource = 'template' | 'model';

export const EXPLANATION_SOURCES: readonly ExplanationSource[] = ['template', 'model'];

export function isExplanationSource(value: unknown): value is ExplanationSource {
  return (['template', 'model'] as readonly unknown[]).includes(value);
}

/**
 * Four outcomes, because they are four different facts and only one of them is anybody's fault.
 * `template_only` — no model is routed for this tenant. A deployment fact. `rejected` — a model
 * answered and the validator refused it. A model-quality fact; the template is shown instead,
 * never a blank and never the refused text. `unavailable` — a model *is* routed and could not be
 * reached. Our outage, not their output; collapsing it with `rejected` would page the wrong
 * person, the same split `SealCheck` draws between UNCHECKABLE and FORGED. The first
 * implementation collapsed `template_only` into `unavailable`, because both surface as the same
 * exception. Found by running it.
 */
export type ExplanationStatus = 'template_only' | 'model_accepted' | 'rejected' | 'unavailable';

export const EXPLANATION_STATUSES: readonly ExplanationStatus[] = ['template_only', 'model_accepted', 'rejected', 'unavailable'];

export function isExplanationStatus(value: unknown): value is ExplanationStatus {
  return (['template_only', 'model_accepted', 'rejected', 'unavailable'] as readonly unknown[]).includes(value);
}

export interface Explanation {
  readonly source: ExplanationSource;
  readonly status: ExplanationStatus;
  /**
   * Host-written, or a validated completion. There is no third path — the service holds the only
   * construction site and has no branch that assigns a raw completion.
   */
  readonly text: string;
  /**
   * Which validator accepted it. Required for a `model` source, at the domain *and* by a CHECK
   * constraint — model prose cannot be stored without naming what checked it.
   */
  readonly validator?: string | null;
  /** A closed-vocabulary reason code from `core.ai.serving`, **never the refused text**. */
  readonly rejection_reason?: string | null;
}

export function isExplanation(value: unknown): value is Explanation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['template', 'model'] as readonly unknown[]).includes(candidate['source']) &&
    (['template_only', 'model_accepted', 'rejected', 'unavailable'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['text'] === 'string'
  );
}

export interface Finding {
  readonly signal: SignalKind;
  readonly verdict: Verdict;
  readonly observed?: number | null;
  readonly baseline?: number | null;
  /**
   * Modified z-score. **Null, never zero, when it could not be computed** — a 0.0 here reads as
   * "perfectly ordinary", which is the opposite of "we could not compute this". The same rule
   * `DatasetDelta` follows for an unmeasurable score.
   */
  readonly deviation?: number | null;
  readonly direction?: string | null;
  readonly sample_size: number;
  readonly critical: boolean;
  /** True only for `anomalous`. Everything else is silence. */
  readonly actionable: boolean;
  readonly priority: Priority;
  /**
   * Lower sorts first. Sent alongside `priority` so a screen listing many findings can order them
   * without re-deriving the ranking `Priority`'s own description states once.
   */
  readonly priority_rank: number;
}

export function isFinding(value: unknown): value is Finding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['volume', 'freshness', 'quality_score'] as readonly unknown[]).includes(candidate['signal']) &&
    (['insufficient_history', 'normal', 'undetermined', 'anomalous'] as readonly unknown[]).includes(candidate['verdict']) &&
    typeof candidate['sample_size'] === 'number' &&
    typeof candidate['critical'] === 'boolean' &&
    typeof candidate['actionable'] === 'boolean' &&
    (['critical', 'anomalous', 'worth_reviewing', 'normal', 'not_yet_measured'] as readonly unknown[]).includes(candidate['priority']) &&
    typeof candidate['priority_rank'] === 'number'
  );
}

export interface SeriesPoint {
  readonly observed_at: string;
  readonly value: number;
}

export function isSeriesPoint(value: unknown): value is SeriesPoint {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['observed_at'] === 'string' &&
    typeof candidate['value'] === 'number'
  );
}

/**
 * The range a point may sit in and still read as ordinary: median ± threshold·MAD/0.6745, over the
 * same baseline history the detector medianed.
 */
export interface BaselineBand {
  readonly low: number;
  readonly high: number;
}

export function isBaselineBand(value: unknown): value is BaselineBand {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['low'] === 'number' &&
    typeof candidate['high'] === 'number'
  );
}

export interface DetectorInfo {
  readonly signal: SignalKind;
  /** Always median / median-absolute-deviation. Never a mean — see `Verdict`. */
  readonly method: string;
  /** How the baseline window is described in words, derived from the bounds. */
  readonly window: string;
  /**
   * The ordinary-anomaly threshold in force for this series, in deviations — the per-series
   * override when one is set, otherwise the deployment default.
   */
  readonly threshold: number;
  /** Past this, severity is critical regardless of the override. Never overridable. */
  readonly critical_threshold: number;
  readonly watches: readonly ('above' | 'below')[];
  /** The steward-set override, or `null` when the series uses the default. */
  readonly threshold_override: number | null;
}

export function isDetectorInfo(value: unknown): value is DetectorInfo {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['volume', 'freshness', 'quality_score'] as readonly unknown[]).includes(candidate['signal']) &&
    typeof candidate['method'] === 'string' &&
    typeof candidate['window'] === 'string' &&
    typeof candidate['threshold'] === 'number' &&
    typeof candidate['critical_threshold'] === 'number' &&
    Array.isArray(candidate['watches']) &&
    (candidate['watches'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (typeof candidate['threshold_override'] === 'number' || candidate['threshold_override'] === null)
  );
}

/**
 * Standing configuration for one watched `(dataset, signal)` pair — what a steward has decided
 * *about* the series, as opposed to what one evaluation *found*. `muted_reason` and
 * `expected_note` are free text that never leaves this surface.
 */
export interface SeriesState {
  readonly muted: boolean;
  readonly muted_at?: string | null;
  readonly muted_by?: string | null;
  readonly muted_reason?: string | null;
  readonly expected: boolean;
  readonly expected_at?: string | null;
  readonly expected_by?: string | null;
  readonly expected_note?: string | null;
  readonly threshold_override: number | null;
}

export function isSeriesState(value: unknown): value is SeriesState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['muted'] === 'boolean' &&
    typeof candidate['expected'] === 'boolean' &&
    (typeof candidate['threshold_override'] === 'number' || candidate['threshold_override'] === null)
  );
}

export interface SignalCapability {
  readonly code: SignalKind;
  /** Below this the verdict is `insufficient_history`. Per signal, never shared. */
  readonly minimum_observations: number;
  readonly watches: readonly ('above' | 'below')[];
  readonly threshold: number;
}

export function isSignalCapability(value: unknown): value is SignalCapability {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['volume', 'freshness', 'quality_score'] as readonly unknown[]).includes(candidate['code']) &&
    typeof candidate['minimum_observations'] === 'number' &&
    Array.isArray(candidate['watches']) &&
    (candidate['watches'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['threshold'] === 'number'
  );
}

export interface AnomalyCapabilities {
  readonly rung: number;
  readonly source_access: string;
  readonly signals: readonly SignalCapability[];
  readonly verdicts: readonly Verdict[];
  readonly detection_method: string;
  /**
   * Published because it is the boundary a customer's security reviewer asks about, and because a
   * reader who cannot see it stated will assume the model decides something.
   */
  readonly model_role: string;
  readonly explanation_statuses: readonly ExplanationStatus[];
  readonly explanation_sources: readonly ExplanationSource[];
}

export function isAnomalyCapabilities(value: unknown): value is AnomalyCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    typeof candidate['source_access'] === 'string' &&
    Array.isArray(candidate['signals']) &&
    (candidate['signals'] as readonly unknown[]).every((item) => isSignalCapability(item)) &&
    Array.isArray(candidate['verdicts']) &&
    (candidate['verdicts'] as readonly unknown[]).every((item) => isVerdict(item)) &&
    typeof candidate['detection_method'] === 'string' &&
    typeof candidate['model_role'] === 'string' &&
    Array.isArray(candidate['explanation_statuses']) &&
    (candidate['explanation_statuses'] as readonly unknown[]).every((item) => isExplanationStatus(item)) &&
    Array.isArray(candidate['explanation_sources']) &&
    (candidate['explanation_sources'] as readonly unknown[]).every((item) => isExplanationSource(item))
  );
}
